import { useMemo, useState } from "react";

import type { Cart } from "@/customer/cart/types";
import type { AuthUser } from "@/customer/auth/AuthContext";

export type CheckoutStepId =
  | "cart"
  | "address"
  | "date"
  | "account"
  | "consent"
  | "coi"
  | "payment";

export interface CheckoutStep {
  id: CheckoutStepId;
  title: string;
  visible: boolean;
  complete: boolean;
  unlocked: boolean;
}

export interface CheckoutFlowInput {
  cart: Cart;
  user: AuthUser | null;
  hasCurrentCoi: boolean;
}

export interface CheckoutFlow {
  steps: Record<CheckoutStepId, CheckoutStep>;
  orderedSteps: CheckoutStepId[];
  firstIncompleteStep: CheckoutStepId | null;
  canSubmit: boolean;
}

const STEP_ORDER: CheckoutStepId[] = [
  "cart",
  "address",
  "date",
  "account",
  "consent",
  "coi",
  "payment"
];

const STEP_TITLES: Record<CheckoutStepId, string> = {
  cart: "Cart contents",
  address: "Delivery address",
  date: "Delivery date and window",
  account: "Account",
  consent: "Drop-off authorization",
  coi: "Certificate of insurance",
  payment: "Payment method"
};

function isAddressComplete(cart: Cart): boolean {
  const a = cart.payload.delivery_address;
  if (!a) return false;
  return Boolean(a.line1 && a.city && a.state && a.postal_code);
}

function isDateComplete(cart: Cart): boolean {
  const p = cart.payload;
  return Boolean(p.delivery_date_start && p.delivery_date_end && p.delivery_window_hours);
}

function isCoiVisible(cart: Cart, hasCurrentCoi: boolean): boolean {
  if (!cart.any_line_requires_coi) return false;
  if (cart.damage_waiver_opted_in) return false;
  return !hasCurrentCoi;
}

// Pure resolver kept separate from the React hook so it can be unit
// tested without rendering. Given the (cart, user, COI status) triple,
// return a fully-derived step list — no parallel state, no drift.
export function resolveCheckoutFlow(input: CheckoutFlowInput): CheckoutFlow {
  const { cart, user, hasCurrentCoi } = input;

  const completeMap: Record<CheckoutStepId, boolean> = {
    cart: cart.payload.items.length > 0,
    address: isAddressComplete(cart),
    date: isDateComplete(cart),
    account: user !== null,
    consent: cart.payload.apparent_agent_consent === true,
    coi: hasCurrentCoi,
    payment: Boolean(cart.payload.payment_method_type)
  };

  const visibleMap: Record<CheckoutStepId, boolean> = {
    cart: true,
    address: true,
    date: true,
    account: true,
    consent: true,
    coi: isCoiVisible(cart, hasCurrentCoi),
    payment: true
  };

  // A step is unlocked iff every *visible* earlier step is complete.
  // Locked steps still render so customers can see the road ahead;
  // their triggers are disabled until the predecessor finishes.
  let earlierComplete = true;
  const unlockedMap: Record<CheckoutStepId, boolean> = {} as Record<CheckoutStepId, boolean>;
  for (const id of STEP_ORDER) {
    unlockedMap[id] = earlierComplete;
    if (visibleMap[id] && !completeMap[id]) earlierComplete = false;
  }

  const steps = STEP_ORDER.reduce<Record<CheckoutStepId, CheckoutStep>>((acc, id) => {
    acc[id] = {
      id,
      title: STEP_TITLES[id],
      visible: visibleMap[id],
      complete: completeMap[id],
      unlocked: unlockedMap[id]
    };
    return acc;
  }, {} as Record<CheckoutStepId, CheckoutStep>);

  const orderedSteps = STEP_ORDER.filter((id) => visibleMap[id]);
  const firstIncompleteStep =
    orderedSteps.find((id) => !completeMap[id]) ?? null;
  const canSubmit = orderedSteps.every((id) => completeMap[id]);

  return { steps, orderedSteps, firstIncompleteStep, canSubmit };
}

// React hook layered on the pure resolver. Owns the "open accordion
// item" state — defaults to the first incomplete step, but lets the
// customer click any unlocked step's header to re-open and edit.
export function useCheckoutFlow(input: CheckoutFlowInput): CheckoutFlow & {
  openStep: CheckoutStepId | null;
  setOpenStep: (id: CheckoutStepId | null) => void;
} {
  const flow = useMemo(() => resolveCheckoutFlow(input), [input]);
  const [override, setOverride] = useState<CheckoutStepId | null>(null);

  // If the customer clicked a step open and that step is no longer
  // unlocked (because an earlier step was edited and broke the chain),
  // drop the override silently rather than showing a locked-open card.
  const openStep =
    override && flow.steps[override]?.unlocked
      ? override
      : flow.firstIncompleteStep;

  return { ...flow, openStep, setOpenStep: setOverride };
}
